import { displayDate, isDeliveryReviewed, itemDelivery, itemDeliveryStatus } from './delivery';
import { multiplyRupees, sumRupees } from './inr';
import { validMappedProduct } from './productMapping';

const number = (value, fallback = 0) => value == null || !Number.isFinite(Number(value)) ? fallback : Number(value);
const normalize = value => String(value || '').normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
export const productReference = value => value && String(typeof value === 'object' ? value._id || '' : value).toLowerCase();

export function expandInvoiceItems(invoices, products = []) {
  const byId = new Map(products.map(product => [productReference(product._id), product]));
  return invoices.flatMap(invoice => (Array.isArray(invoice.items) ? invoice.items : []).map((raw, index) => {
    const item = raw || {};
    const originalProductId = productReference(item.productId);
    const product = validMappedProduct(item, invoice.organization, byId);
    const productId = product ? productReference(product._id) : null;
    const { ordered: qty, delivered: deliveredQty, remaining: remainder } = itemDelivery(item);
    const reviewed = isDeliveryReviewed(invoice);
    return {
      key: `${invoice._id}-${item._id || index}`, invoiceId: invoice._id,
      invoiceDate: invoice.invoiceDate || invoice.date, invoiceNumber: invoice.invoiceNumber || invoice._id,
      customerName: invoice.customerName || '—', whatsapp: invoice.whatsapp || '—', organization: invoice.organization || '—',
      productId, originalProductId, mappingIssue: !!originalProductId && !product, item: item.label || product?.name || 'Unnamed item', productName: product?.name || '',
      sku: product?.sku || '', qty, deliveredQty, pendingQty: remainder, uncertain: !reviewed || itemDelivery(item).uncertain, confirmedPendingQty: reviewed ? remainder : null,
      unreviewedPotentialQty: reviewed ? 0 : remainder,
      deliveryStatus: itemDeliveryStatus(item, reviewed),
      reviewStatus: reviewed ? 'Confirmed' : 'Historical uncertainty', unitAmount: number(item.amount), lineAmount: qty === null ? null : multiplyRupees(number(item.amount), qty, true)
    };
  }));
}

export function filterItemRows(rows, term = '', status = 'All') {
  const search = normalize(term);
  return rows.filter(row => (!search || normalize(`${row.item} ${row.productName} ${row.sku}`).includes(search)) &&
    (status === 'All' || row.deliveryStatus === status));
}

export function sortItemRows(rows, sort = 'item') {
  const compare = (a, b) => String(a || '').trim().localeCompare(String(b || '').trim(), undefined, { numeric: true, sensitivity: 'base' });
  return [...rows].sort((a, b) => {
    if (sort === 'date') return (Date.parse(b.invoiceDate) || 0) - (Date.parse(a.invoiceDate) || 0) || compare(a.key, b.key);
    const field = sort === 'customer' ? 'customerName' : sort === 'order' ? 'invoiceNumber' : 'item';
    return compare(a[field], b[field]) || compare(a.invoiceNumber, b.invoiceNumber) || compare(a.key, b.key);
  });
}

export function quantityTotals(rows) {
  return rows.reduce((sum, row) => ({
    qty: sum.qty + (row.qty ?? 0), deliveredQty: sum.deliveredQty + (row.deliveredQty ?? 0),
    pendingQty: sum.pendingQty + (row.pendingQty ?? 0), unknownPendingRows: sum.unknownPendingRows + (row.pendingQty === null ? 1 : 0),
    confirmedPendingQty: sum.confirmedPendingQty + (row.confirmedPendingQty ?? 0),
    unreviewedPotentialQty: sum.unreviewedPotentialQty + row.unreviewedPotentialQty,
    value: sumRupees([sum.value, row.lineAmount ?? 0], true)
  }), { qty: 0, deliveredQty: 0, pendingQty: 0, unknownPendingRows: 0, confirmedPendingQty: 0, unreviewedPotentialQty: 0, value: 0 });
}

export function summarizeProducts(rows) {
  const groups = new Map();
  rows.forEach(row => {
    // IDs distinguish mapped products even when names match. Legacy descriptions
    // are scoped by organization so unrelated business orders are not combined.
    const key = row.productId ? `product:${row.productId}` : `legacy:${JSON.stringify([row.organization, normalize(row.item)])}`;
    if (!groups.has(key)) groups.set(key, { key, item: row.productName || row.item, sku: row.sku,
      productId: row.productId, organization: row.organization, qty: 0, deliveredQty: 0, pendingQty: 0, unknownPendingRows: 0, confirmedPendingQty: 0, unreviewedPotentialQty: 0 });
    const group = groups.get(key);
    group.qty += row.qty ?? 0; group.deliveredQty += row.deliveredQty ?? 0;
    group.pendingQty += row.pendingQty ?? 0; group.unknownPendingRows += row.pendingQty === null ? 1 : 0;
    group.confirmedPendingQty += row.confirmedPendingQty ?? 0;
    group.unreviewedPotentialQty += row.unreviewedPotentialQty;
  });
  return [...groups.values()].sort((a, b) => a.item.trim().localeCompare(b.item.trim(), undefined, { sensitivity: 'base' }) || a.key.localeCompare(b.key));
}

export function itemExportTable(rows, summary = false) {
  if (summary) return {
    headers: ['Product / Item', 'Mapping', 'Product ID', 'Organization', 'Ordered Qty', 'Recorded Delivered Qty', 'Pending Qty'],
    body: rows.map(row => [row.item, row.productId ? 'Product Master' : 'Legacy / Unmapped', row.productId || '', row.organization,
      row.qty, row.deliveredQty, row.unknownPendingRows ? `${row.pendingQty} (partial: unknown quantities)` : row.pendingQty])
  };
  return {
    headers: ['Invoice Date', 'Invoice Number', 'Customer Name', 'WhatsApp', 'Organization', 'Product / Item', 'Ordered Qty', 'Delivered Qty', 'Pending Qty', 'Delivery Status', 'Unit Price (INR)', 'Line Amount (INR)'],
    body: rows.map(row => [displayDate(row.invoiceDate), row.invoiceNumber, row.customerName, row.whatsapp, row.organization,
      row.item, row.qty ?? 'Unknown', row.deliveredQty ?? 'Unknown', row.pendingQty ?? 'Unknown', row.deliveryStatus, row.unitAmount, row.lineAmount ?? 'Unknown'])
  };
}

export function csvText(table) {
  const escape = value => {
    // Keep spreadsheet applications from interpreting user-entered text as formulas.
    let text = String(value ?? '');
    if (typeof value === 'string' && (/^[\s]*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text))) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  };
  return '\uFEFF' + [table.headers, ...table.body].map(row => row.map(escape).join(',')).join('\r\n');
}

export function downloadCsv(table, filename) {
  const url = URL.createObjectURL(new Blob([csvText(table)], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
